import { useState, useEffect, useCallback } from 'react'
import { tourApi } from '../api/tourApi'

/**
 * Custom hook para validaciones de tours
 * Implementa validación en tiempo real y cache de resultados
 */
export const useTourValidation = (tourId, tourData = {}) => {
  const [validation, setValidation] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [validationCache, setValidationCache] = useState(new Map())

  // Generar clave de cache para validaciones
  const generateValidationCacheKey = useCallback((data) => {
    const keyData = {
      tourId,
      vehicle_id: data.vehicle_id,
      gpsdevice_id: data.gpsdevice_id,
      r_gps: data.r_gps,
      r_insurance: data.r_insurance,
      r_cancel_insurance: data.r_cancel_insurance,
      r_single: data.r_single,
      low_seat: data.low_seat,
      discount: data.discount,
      rewards: data.rewards,
      value_to_charge: data.value_to_charge
    }
    return JSON.stringify(keyData)
  }, [tourId])

  // Función para validar datos del tour
  const validateTour = useCallback(async (data = {}) => {
    if (!tourId) {
      setError('ID de tour requerido')
      return
    }

    const cacheKey = generateValidationCacheKey(data)
    
    // Verificar cache de validaciones
    if (validationCache.has(cacheKey)) {
      const cached = validationCache.get(cacheKey)
      if (Date.now() - cached.timestamp < 2 * 60 * 1000) { // 2 minutos
        setValidation(cached.data)
        setLoading(false)
        setError(null)
        return
      }
    }

    setLoading(true)
    setError(null)

    try {
      const result = await tourApi.validateTour(tourId, data)
      
      if (result.success) {
        // Guardar en cache
        setValidationCache(prev => new Map(prev.set(cacheKey, {
          data: result.validation,
          timestamp: Date.now()
        })))
        
        setValidation(result.validation)
      } else {
        setError(result.error || 'Error al validar tour')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
    } finally {
      setLoading(false)
    }
  }, [tourId, generateValidationCacheKey, validationCache])

  // Validar cuando cambian los datos
  useEffect(() => {
    if (tourId) {
      validateTour(tourData)
    }
  }, [tourId, tourData, validateTour])

  // Función para validar manualmente
  const revalidate = useCallback((newData) => {
    validateTour(newData)
  }, [validateTour])

  // Función para validación en tiempo real
  const validateField = useCallback(async (field, value) => {
    const fieldValidation = {
      [field]: value
    }

    try {
      const result = await tourApi.validateField(tourId, fieldValidation)
      return result.validation
    } catch (err) {
      return { isValid: false, errors: [err.message] }
    }
  }, [tourId])

  // Función para limpiar cache de validaciones
  const clearValidationCache = useCallback(() => {
    setValidationCache(new Map())
  }, [])

  // Función para obtener estadísticas del cache de validaciones
  const getValidationCacheStats = useCallback(() => {
    const entries = Array.from(validationCache.entries())
    const now = Date.now()
    
    const validEntries = entries.filter(([key, value]) => 
      now - value.timestamp < 2 * 60 * 1000
    )

    return {
      totalEntries: entries.length,
      validEntries: validEntries.length,
      expiredEntries: entries.length - validEntries.length,
      cacheSize: validationCache.size
    }
  }, [validationCache])

  // Función para validación local básica
  const validateLocally = useCallback((data) => {
    const errors = []
    const warnings = []

    // Validaciones básicas
    if (data.value_to_charge && data.value_to_charge < 0) {
      errors.push('Valor a cobrar no puede ser negativo')
    }

    if (data.discount && data.discount < 0) {
      errors.push('Descuento no puede ser negativo')
    }

    if (data.rewards && data.rewards < 0) {
      errors.push('Rewards no pueden ser negativos')
    }

    if (data.r_gps && !data.gpsdevice_id) {
      warnings.push('GPS seleccionado pero no se especificó dispositivo')
    }

    if (data.r_insurance && data.r_cancel_insurance) {
      warnings.push('Tener ambos seguros puede ser redundante')
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    }
  }, [])

  return {
    validation,
    loading,
    error,
    revalidate,
    validateField,
    validateLocally,
    clearValidationCache,
    getValidationCacheStats
  }
} 


// WEBPACK FOOTER //
// ./src/features/tours/hooks/useTourValidation.js